import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Loader2, CreditCard, CheckCircle2, XCircle, Landmark } from "lucide-react";
import { type MpBrickController, unmountBrickSafely } from "@/lib/mp-brick";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useDirectPayment, type DirectPaymentResult } from "@/hooks/useDirectPayment";
import { PsePaymentForm } from "@/components/settings/PsePaymentForm";
import { isPseMode } from "@/lib/pse-payment";
import { directPayerEmailError, normalizeDirectPayerEmail } from "@/lib/mercadopago-payer-email";

const MP_PUBLIC_KEY = import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY || "";

interface DirectPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  amount: number;
  description?: string;
  planId?: string;
  extraPackId?: string;
  planUpgradeRequestId?: string;
  payerEmail?: string;
  mode?: string;
  onSuccess?: (result: DirectPaymentResult) => void;
  onError?: (error: string) => void;
}

type CardFormData = {
  token: string;
  payment_method_id: string;
  installments: number;
  payer: { email: string; identification: { type: string; number: string } };
  issuer_id: string;
};

declare global {
  interface Window {
    MercadoPago: new (key: string, opts: { locale: string }) => {
      bricks: () => {
        create: (type: string, id: string, config: Record<string, unknown>) => Promise<MpBrickController>;
      };
    };
  }
}

export function DirectPaymentDialog({
  open,
  onOpenChange,
  orgId,
  amount,
  description,
  planId,
  extraPackId,
  planUpgradeRequestId,
  payerEmail,
  mode,
  onSuccess,
  onError,
}: DirectPaymentDialogProps) {
  const brickContainerRef = useRef<HTMLDivElement>(null);
  const brickControllerRef = useRef<MpBrickController | null>(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;
  const submittedRef = useRef(false);

  const teardownBrick = useCallback(() => {
    const ctrl = brickControllerRef.current;
    brickControllerRef.current = null;
    unmountBrickSafely(ctrl);
  }, []);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const { pay, loading: paying, result, reset } = useDirectPayment();
  const pseAvailable = isPseMode(mode);
  const [method, setMethod] = useState<"card" | "pse">("card");
  const [payerEmailDraft, setPayerEmailDraft] = useState(payerEmail ?? "");
  const [payerEmailError, setPayerEmailError] = useState<string | null>(null);
  const [confirmedPayerEmail, setConfirmedPayerEmail] = useState<string | null>(null);
  const paymentIntentId = useMemo(() => crypto.randomUUID(), [open, retryCount, method, confirmedPayerEmail]);

  useEffect(() => {
    if (!open) return;
    setMethod("card");
    setPayerEmailDraft(payerEmail ?? "");
    setPayerEmailError(null);
    setConfirmedPayerEmail(null);
  }, [open, payerEmail]);

  const confirmPayerEmail = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const error = directPayerEmailError(payerEmailDraft);
    setPayerEmailError(error);
    if (!error) setConfirmedPayerEmail(normalizeDirectPayerEmail(payerEmailDraft));
  };

  const loadMercadoPagoSDK = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.MercadoPago) {
        resolve();
        return;
      }
      const existing = document.querySelector('script[src*="sdk.mercadopago.com"]') as HTMLScriptElement | null;
      if (existing) {
        let attempts = 0;
        const check = () => {
          if (window.MercadoPago) { resolve(); return; }
          if (++attempts > 200) { reject(new Error("MercadoPago SDK timeout")); return; }
          setTimeout(check, 50);
        };
        check();
        return;
      }
      const script = document.createElement("script");
      script.src = "https://sdk.mercadopago.com/js/v2";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load MercadoPago SDK"));
      document.head.appendChild(script);
    });
  }, []);

  const mountBrick = useCallback(async () => {
    if (!brickContainerRef.current || !MP_PUBLIC_KEY || !confirmedPayerEmail) return;

    try {
      await loadMercadoPagoSDK();
      const mp = new window.MercadoPago(MP_PUBLIC_KEY, { locale: "es-CO" });
      const bricksBuilder = mp.bricks();

      teardownBrick();
      brickContainerRef.current.innerHTML = "";

      const controller = await bricksBuilder.create("cardPayment", "mp-card-payment-container", {
        initialization: { amount, payer: { email: confirmedPayerEmail } },
        customization: {
          visual: { hideFormTitle: true, style: { theme: "flat" } },
          paymentMethods: { maxInstallments: 1, minInstallments: 1 },
        },
        callbacks: {
          onReady: () => setSdkReady(true),
          onSubmit: async (cardFormData: CardFormData) => {
            if (submittedRef.current) return;
            submittedRef.current = true;

            const res = await pay({
              organization_id: orgId,
              token: cardFormData.token,
              payment_method_id: cardFormData.payment_method_id,
              amount,
              installments: cardFormData.installments || 1,
              payer_email: confirmedPayerEmail,
              description,
              plan_id: planId,
              extra_pack_id: extraPackId,
              plan_upgrade_request_id: planUpgradeRequestId,
              mode,
              payment_intent_id: paymentIntentId,
            });

            if (res.success) {
              teardownBrick();
              onSuccessRef.current?.(res);
            } else {
              submittedRef.current = false;
              onErrorRef.current?.(res.error || "Error al procesar el pago");
            }
          },
          onError: (error: unknown) => {
            console.error("[DirectPaymentDialog] Brick error:", error);
          },
        },
      });

      brickControllerRef.current = controller;
    } catch (err: unknown) {
      console.error("[DirectPaymentDialog] SDK/Brick init error:", err);
      const message = err instanceof Error ? err.message : "No se pudo cargar el formulario de pago";
      setSdkError(message);
    }
  }, [amount, orgId, confirmedPayerEmail, description, planId, extraPackId, planUpgradeRequestId, mode, paymentIntentId, pay, loadMercadoPagoSDK, teardownBrick]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (open && MP_PUBLIC_KEY && method === "card" && confirmedPayerEmail) {
      setSdkReady(false);
      setSdkError(null);
      submittedRef.current = false;
      reset();
      timer = setTimeout(mountBrick, 100);
    }
    return () => {
      if (timer) clearTimeout(timer);
      teardownBrick();
    };
  }, [open, method, confirmedPayerEmail, mountBrick, reset, retryCount, teardownBrick]);

  if (!MP_PUBLIC_KEY) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="md">
          <Alert className="border-destructive/30 bg-destructive/5">
            <AlertDescription>
              Configuración de pagos incompleta. Contacta al administrador.
            </AlertDescription>
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  const approved = result?.status === "approved";
  const rejected = result && !result.success;

  return (
    <Dialog open={open} onOpenChange={paying ? undefined : onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {method === "pse" ? <Landmark className="h-5 w-5 text-primary" /> : <CreditCard className="h-5 w-5 text-primary" />}
            {method === "pse" ? "Pago con PSE" : "Pago con tarjeta"}
          </DialogTitle>
          <DialogDescription>
            {description || "Pago directo"} —{" "}
            <span className="font-semibold text-foreground">
              ${amount.toLocaleString("es-CO")} COP
            </span>
          </DialogDescription>
        </DialogHeader>

        {pseAvailable && !result && !paying && (
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Medio de pago">
            {(["card", "pse"] as const).map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={method === option}
                onClick={() => setMethod(option)}
                className={`flex items-center justify-center gap-2 rounded-xl border-2 p-2.5 text-sm font-medium transition-colors ${method === option ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
              >
                {option === "card" ? <CreditCard className="h-4 w-4" /> : <Landmark className="h-4 w-4" />}
                {option === "card" ? "Tarjeta" : "PSE"}
              </button>
            ))}
          </div>
        )}

        {confirmedPayerEmail && !result && !paying && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-xs">
            <span className="min-w-0 truncate text-muted-foreground">
              Paga: <span className="font-medium text-foreground">{confirmedPayerEmail}</span>
            </span>
            <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setConfirmedPayerEmail(null)}>
              Cambiar
            </Button>
          </div>
        )}

        <DialogBody>
          {!confirmedPayerEmail ? (
            <form className="space-y-3" onSubmit={confirmPayerEmail} noValidate>
              <div className="space-y-2">
                <Label htmlFor="direct-payment-payer-email">Correo de quien paga</Label>
                <Input
                  id="direct-payment-payer-email"
                  type="email"
                  autoComplete="email"
                  placeholder="pagos@tuempresa.com"
                  value={payerEmailDraft}
                  onChange={(e) => {
                    setPayerEmailDraft(e.target.value);
                    if (payerEmailError) setPayerEmailError(null);
                  }}
                  aria-invalid={payerEmailError ? true : undefined}
                  aria-describedby={payerEmailError ? "direct-payment-payer-email-help direct-payment-payer-email-error" : "direct-payment-payer-email-help"}
                />
                {payerEmailError && (
                  <p id="direct-payment-payer-email-error" role="alert" className="text-xs font-medium text-destructive">
                    {payerEmailError}
                  </p>
                )}
                <p id="direct-payment-payer-email-help" className="text-xs text-muted-foreground">
                  A este correo llegan el código de PSE y el comprobante de MercadoPago.
                </p>
              </div>
              <Button type="submit" className="w-full">Continuar</Button>
            </form>
          ) : pseAvailable && method === "pse" && mode ? (
            <PsePaymentForm
              key={paymentIntentId}
              orgId={orgId}
              amount={amount}
              description={description}
              payerEmail={confirmedPayerEmail}
              mode={mode}
              paymentIntentId={paymentIntentId}
              onError={(message) => onErrorRef.current?.(message)}
            />
          ) : approved ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
              <p className="text-center font-medium">¡Pago aprobado!</p>
              <p className="text-xs text-muted-foreground text-center">
                ID de pago: {result.payment_id}
              </p>
              <Button onClick={() => onOpenChange(false)}>Cerrar</Button>
            </div>
          ) : rejected ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <XCircle className="h-12 w-12 text-destructive" />
              <p className="text-center font-medium">El pago no fue aprobado</p>
              <p className="text-xs text-muted-foreground text-center">{result.error}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
                <Button onClick={() => { reset(); setRetryCount((c) => c + 1); }}>
                  Reintentar
                </Button>
              </div>
            </div>
          ) : sdkError ? (
            <Alert className="border-destructive/30 bg-destructive/5">
              <AlertDescription>{sdkError}</AlertDescription>
            </Alert>
          ) : (
            <>
              {(!sdkReady || paying) && (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {paying ? "Procesando pago..." : "Cargando formulario..."}
                </div>
              )}
              <div
                id="mp-card-payment-container"
                ref={brickContainerRef}
                className={!sdkReady ? "hidden" : ""}
              />
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
